import { useAuthenticationState } from 'app/authentication/state';
import { UserRole, hasRole } from 'app/authentication/UserRole';
import { AddFiber } from 'app/fibers/components/AddFiber';
import { PremiseOnSinglePremise } from 'app/premises/Premise';
import { useTranslation } from 'common/hooks/useTranslation';
import * as commonStyles from 'common/styles';
import { Lambda } from 'common/types';
import { FC, useCallback, useState } from 'react';
import { useBoolean } from 'usehooks-ts';
import { G } from '@mobily/ts-belt';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Button, Grid, Paper, Typography } from '@mui/material';
import { AvailableOutletTypes } from 'app/outlets/AvailiableOutletTypes';
import { EditFiberDialog } from 'app/fibers/components/components/EditFiberDialog';
import { EditFiberForm } from 'app/fibers/components/components/EditFiberForm';
import { Fiber, Outlet } from '../../../Outlet';
import { HowToFindOutlet } from '../../HowToFindOutlet/HowToFindOutlet';
import { OutletMenu } from '../../OutletMenu/OutletMenu';
import { OutletPhotos } from '../../OutletPhotos/OutletPhotos';
import { DeleteFiber } from './DeleteFiber';
import { DeleteFiberDialog } from './DeleteFiberDialog';
import { FiberDetails } from './FiberDetails';
import * as styles from './styles';
import { OutletContactType } from '../../OutletContactType/OutletContactType';

export const SinglePremiseOutlet: FC<{
  outlet: Outlet;
  premise: PremiseOnSinglePremise;
  onDelete?: Lambda<void, void>;
}> = ({ outlet, premise, onDelete }) => {
  const { t } = useTranslation();

  const { value: isDeleteFiberOpen, setValue: setIsDeleteFiberOpen } = useBoolean(false);
  const { value: isEditFiberOpen, setValue: setIsEditFiberOpen } = useBoolean(false);
  const [fiberToDelete, setFiberToDelete] = useState<null | Fiber>(null);
  const [fiberToEdit, setFiberToEdit] = useState<null | Fiber>(null);
  const { userId, roles } = useAuthenticationState();

  const canUserDeleteFiber = useCallback(
    (fiber: Fiber) => {
      if (hasRole(roles, UserRole.Admin) || hasRole(roles, UserRole.ContractorPM)) return true;

      const fiberCreatorId = fiber.personId;

      if (fiberCreatorId !== null && userId === fiberCreatorId) return true;

      return false;
    },
    [userId, roles],
  );

  return (
    <>
      <DeleteFiberDialog
        isOpen={G.isNotNullable(fiberToDelete) && isDeleteFiberOpen}
        onClose={() => {
          setFiberToDelete(null);
          setIsDeleteFiberOpen(false);
        }}
      >
        {G.isNotNullable(fiberToDelete) && (
          <DeleteFiber
            outlet={outlet}
            premise={premise}
            fiber={fiberToDelete}
            onCancel={() => {
              setFiberToDelete(null);
              setIsDeleteFiberOpen(false);
            }}
            onSuccess={() => {
              setFiberToDelete(null);
              setIsDeleteFiberOpen(false);
            }}
          />
        )}
      </DeleteFiberDialog>
      <EditFiberDialog
        isOpen={isEditFiberOpen}
        onClose={() => {
          setIsEditFiberOpen(false);
          setFiberToEdit(null);
        }}
        outletType={AvailableOutletTypes.OUTLET}
      >
        {fiberToEdit && (
          <EditFiberForm
            fiber={fiberToEdit}
            outletId={outlet.id}
            propertyId={premise.propertyId}
            onSuccess={() => {
              setIsEditFiberOpen(false);
              setFiberToEdit(null);
            }}
          />
        )}
      </EditFiberDialog>
      <Grid container sx={styles.outlet}>
        <Paper sx={commonStyles.card}>
          <Grid container sx={styles.cardTop}>
            <Typography sx={styles.photosHeader}>{t('premise.photos')}</Typography>
            <OutletMenu
              outlet={outlet}
              premise={premise}
              onDelete={onDelete}
              outletType={AvailableOutletTypes.OUTLET}
            />
          </Grid>
          <OutletPhotos outlet={outlet} premise={premise} outletType={AvailableOutletTypes.OUTLET} />
          <OutletContactType outlet={outlet} premise={premise} />
          <HowToFindOutlet outlet={outlet} premise={premise} />
          <Grid container sx={styles.fibersContainer}>
            {outlet.fibers.map((fiber) => {
              return (
                <Accordion disableGutters square key={fiber.id} data-cy={`fiber-accordion-${fiber.toPort}`}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon titleAccess="accordion-expand" />}
                    data-cy="fiber-accordion-header"
                  >
                    {fiber.toPort}
                  </AccordionSummary>
                  <AccordionDetails data-cy="fiber-accordion-details">
                    <FiberDetails fiber={fiber} />
                    <Grid sx={styles.actionFiberContainer}>
                      <Button
                        variant="text"
                        color="primary"
                        onClick={() => {
                          setFiberToEdit(fiber);
                          setIsEditFiberOpen(true);
                        }}
                      >
                        <EditIcon /> {t('fiber.edit.button')}
                      </Button>
                      {canUserDeleteFiber(fiber) && (
                        <Button
                          variant="text"
                          color="error"
                          onClick={() => {
                            setFiberToDelete(fiber);
                            setIsDeleteFiberOpen(true);
                          }}
                        >
                          <DeleteIcon /> {t('fiber.delete.button')}
                        </Button>
                      )}
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Grid>
          <Grid container sx={styles.centerContainer}>
            <AddFiber premise={premise} outlet={outlet} outletType={AvailableOutletTypes.OUTLET} />
          </Grid>
        </Paper>
      </Grid>
    </>
  );
};
